import React from 'react';
import { PortalProvider } from '@gorhom/portal';

import { DeliveryInFlightModal, LocationWarningModal } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';

import type { LocationScreenProps } from '../../navigation/AppNavigation.props';
import { useDeliveryMenuNavigation } from '../DeliveryMenuScreen/DeliveryMenu.navigation';
import { LocationLayout, MapView } from './components';
import { useLocationSearchTypePicker } from './hooks';
import { useLocationScreenData } from './LocationScreen.data';

export const LocationScreen = (props: LocationScreenProps) => {
  const { navigation } = props;
  const data = useLocationScreenData();

  const {
    locationSearchType,
    locations,
    loading,
    deliveryInFlightWarning,
    inFlightDeliveryOrder,
    selectedLocation,
    isShownLocationWarningModal,
    setLocationSearchType,
    setSelectedLocation,
    hideDeliveryInFlightWarning,
    hideLocationWarningModal,
    onLocationWarningPress,
    fetchLocationsBasedOnUserGeolocation,
    userLocation,
    mapOffset,
  } = data;

  const isMapsAutoFitBoundsEnabled = useFeatureFlag(
    'XP-3179-short-maps-auto-bound-enabled',
  );
  const isMapsOffsetEnabled = useFeatureFlag(
    'XP-3179-short-maps-offset-enabled',
  );

  const { handleGoHome, handleViewOrder } = useDeliveryMenuNavigation();

  // The location search type picker is part of the header.
  const shouldDisableSearchControlsWhileLoading = useFeatureFlag(
    'XP-3189-short-disable-search-controls-while-fetching-locations',
  );
  const shouldDisableTypePicker =
    shouldDisableSearchControlsWhileLoading && loading;

  const locationSearchTypePicker = useLocationSearchTypePicker({
    navigation,
    locationSearchType,
    setLocationSearchType,
    setSelectedLocation,
    isDisabled: shouldDisableTypePicker,
  });

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => locationSearchTypePicker,
    });
  }, [navigation, locationSearchTypePicker]);

  // fetch available locations based on user's geolocation on screen mount
  React.useEffect(() => {
    if (userLocation.permissionStatus === 'denied') return;

    void fetchLocationsBasedOnUserGeolocation({
      showWarningIfGeolocationDisabled: false,
    });
  }, [fetchLocationsBasedOnUserGeolocation, userLocation.permissionStatus]);

  return (
    <PortalProvider>
      <LocationLayout
        locationSearchTypePicker={locationSearchTypePicker}
        {...data}
      >
        <MapView
          onGeolocateUserBtnPress={fetchLocationsBasedOnUserGeolocation}
          offsetY={mapOffset}
          isAutoFitBoundsEnabled={isMapsAutoFitBoundsEnabled}
          isOffsetEnabled={isMapsOffsetEnabled}
          {...data}
        />
      </LocationLayout>

      {deliveryInFlightWarning && inFlightDeliveryOrder ? (
        <DeliveryInFlightModal
          orderId={inFlightDeliveryOrder}
          onClose={hideDeliveryInFlightWarning}
          onGoHome={handleGoHome}
          onViewOrder={handleViewOrder}
        />
      ) : null}

      {isShownLocationWarningModal ? (
        <LocationWarningModal
          locations={locations}
          onClose={hideLocationWarningModal}
          onConfirm={onLocationWarningPress}
          restaurantSlug={selectedLocation}
        />
      ) : null}
    </PortalProvider>
  );
};
